import { useTutorialAnalytics } from "~/hooks/useTutorialAnalytics"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { ListSection } from "~/components/SectionHeader"
import { IssueRow } from "~/components/issuePage/IssueRow"
import { StatusIcon } from "~/components/icons/status"
import { TutorialContext } from "~/components/tutorials/context"
import { OnboardingStep } from "~/components/tutorials/OnboardingStep"
import { Badge } from "~/components/ui/badge"
import { Button } from "~/components/ui/button"
import { useOnboardingData } from "~/hooks/useOnboardingData"
import { authClient } from "~/lib/auth-client"
import { IssueStatus } from "~/lib/enums"
import { enumFormat } from "~/lib/utils"
import { tutorials } from "~/lib/tutorials"
import { useContext, useEffect, useRef, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"
import IssuePage from "~/components/issuePage/IssuePage"
import { ErrorState, LoadingState } from "~/components/screens/StateScreen"
import { Sheet, SheetContent, SheetTitle } from "~/components/ui/sheet"
import { useTeam } from "~/hooks/useTeam"
import { api } from "~/trpc/react"
import IssueDetailsPage from "./issue-details/page"

function LiveQueue() {
	const { team, isLoading: isLoadingTeam } = useTeam()
	const utils = api.useUtils()
	const [searchParams, setSearchParams] = useSearchParams()
	const selectedIssue = searchParams.get("issue")
	const returnFocus = useRef<HTMLElement | null>(null)
	const {
		data: teamIssueData,
		isLoading: isLoadingIssues,
		error: issuesError
	} = api.issues.getTeamIssues.useQuery(
		{ teamId: team?.id ?? "", filter: { status: "Queue" } },
		{ enabled: !!team?.id }
	)
	api.issues.onUpdate.useSubscription(
		{ teamId: team?.id ?? "" },
		{
			enabled: !!team?.id,
			onData: () => {
				void utils.issues.getTeamIssues.invalidate()
				void utils.user.getTeams.invalidate()
			}
		}
	)
	if (isLoadingTeam || isLoadingIssues) return <LoadingState />
	if (issuesError || !teamIssueData)
		return (
			<ErrorState
				title="Unable to load issues."
				description={issuesError?.message ?? "Team issues are unavailable."}
			/>
		)
	return (
		<>
			<IssuePage
				queueMode
				teamIssueData={teamIssueData}
				onIssueOpen={(issue) => {
					returnFocus.current =
						document.activeElement instanceof HTMLElement
							? document.activeElement
							: null
					setSearchParams((current) => {
						current.set("issue", String(issue.friendlyId))
						return current
					})
				}}
			/>
			<Sheet
				modal={false}
				open={!!selectedIssue}
				onOpenChange={(open) => {
					if (!open)
						setSearchParams((current) => {
							current.delete("issue")
							return current
						})
				}}
			>
				<SheetContent
					size="preview"
					aria-describedby={undefined}
					onInteractOutside={(event) => event.preventDefault()}
					onCloseAutoFocus={(event) => {
						event.preventDefault()
						returnFocus.current?.focus()
					}}
				>
					<SheetTitle className="sr-only">Issue preview</SheetTitle>
					{selectedIssue && (
						<IssueDetailsPage
							key={`${team?.id}-${selectedIssue}`}
							previewFriendlyId={selectedIssue}
							onClosePreview={() =>
								setSearchParams((current) => {
									current.delete("issue")
									return current
								})
							}
						/>
					)}
				</SheetContent>
			</Sheet>
		</>
	)
}

export default function Page() {
	const coordinator = useContext(TutorialContext)
	const { pending, claim, release, ready } = coordinator
	const { team } = useTeam()
	const location = useLocation()
	const { data: session } = authClient.useSession()
	const [active, setActive] = useState(false)
	const owner = useRef(Symbol("queue-v1"))
	useEffect(() => {
		if (
			!ready ||
			!session?.user.id ||
			!team?.enableQueue ||
			pending?.id !== "queue-v1" ||
			(pending.destination && pending.destination !== location.pathname)
		)
			return
		const token = owner.current
		if (!claim(token)) return
		setActive(true)
		return () => {
			setActive(false)
			release(token)
		}
	}, [
		ready,
		session?.user.id,
		team?.id,
		team?.enableQueue,
		pending?.id,
		pending?.destination,
		location.pathname,
		claim,
		release
	])
	return active && pending?.id === "queue-v1" ? (
		<QueuePractice onExit={() => release(owner.current)} />
	) : (
		<LiveQueue />
	)
}

function QueuePractice({ onExit }: { onExit: () => void }) {
	const practice = useOnboardingData("queue-v1")
	const [step, setStep] = useState(1)
	const [viewIssue, setViewIssue] = useState(false)
	const analytics = useTutorialAnalytics("queue-v1", step - 1)
	const utils = api.useUtils()
	const finish = api.user.finishTutorial.useMutation({
		onSuccess: () => {
			void utils.user.getTutorialPreferences.invalidate()
		},
		onError: () =>
			toast.error("Couldn't save tutorial progress. Please try again.")
	})

	const tasks = tutorials["queue-v1"].steps
	const accepted = practice.queueDecision === "accept"
	const task = tasks[step - 1]!
	const needsReset =
		step === 3 && !accepted && practice.issue.status !== IssueStatus.Queue
	const coach =
		!practice.reviewOpen && !needsReset && !viewIssue ? (
			<OnboardingStep
				step={step}
				total={tasks.length}
				task={task}
				complete={task.id !== "accept" || accepted}
				pending={finish.isPending}
				onBack={() => setStep(step - 1)}
				onAdvance={async (skipped) => {
					if (finish.isPending) return
					analytics.finishStep(skipped)
					if (step === tasks.length || (task.id === "accept" && skipped)) {
						try {
							const outcome = accepted ? "completed" : "dismissed"
							await finish.mutateAsync({ tutorialId: "queue-v1", outcome })
							analytics.end(
								outcome,
								accepted ? "finished" : "acceptance_skipped"
							)
							onExit()
						} catch {
							/* Keep the tour open for retry. */
						}
					} else setStep(step + 1)
				}}
			/>
		) : null
	const actions = (
		<>
			<Badge variant="secondary">Practice mode; nothing will be saved.</Badge>
			{viewIssue && (
				<Button variant="ghost" size="sm" onClick={() => setViewIssue(false)}>
					Back to issues
				</Button>
			)}
			{needsReset && (
				<Button
					size="sm"
					onClick={async () => {
						await practice.edit({
							id: practice.issue.id,
							teamId: practice.team.id,
							status: IssueStatus.Queue
						})
						practice.setQueueDecision(undefined)
					}}
				>
					Try accepting again
				</Button>
			)}
			<Button
				variant="ghost"
				size="sm"
				disabled={finish.isPending}
				onClick={() => {
					analytics.end("exited", "exit_button")
					onExit()
				}}
			>
				Exit tour
			</Button>
		</>
	)
	if (step < tasks.length || viewIssue)
		return (
			<IssueDetailsPage
				practice={practice}
				tutorialPrompt={coach}
				tutorialActions={actions}
			/>
		)
	return (
		<main className="flex h-full min-h-0 flex-col">
			<AppHeader actions={actions}>
				<h1 className="font-semibold">Biteclub / Issues</h1>
			</AppHeader>
			<div className="flex min-h-0 flex-1 flex-col gap-5 p-6">
				{coach}
				<div data-tutorial="accepted-issue">
					<ListSection
						title={enumFormat(practice.issue.status)}
						count={1}
						itemName="issue"
						icon={<StatusIcon status={practice.issue.status} />}
					>
						<IssueRow
							practice
							issue={{
								...practice.issue,
								team: {
									...practice.team,
									organization: { slug: "practice", name: "Biteclub" }
								}
							}}
							onOpen={() => setViewIssue(true)}
						/>
					</ListSection>
				</div>
			</div>
		</main>
	)
}
